import { useState, useEffect } from 'react'
import { formatCurrency } from 'lib/helpers'
import { useAppStore } from 'lib/hooks/useAppStore'

import { motion, AnimatePresence } from 'motion/react'
import {
  ProductProvider,
  useProduct,
  AddToCartButton,
  useCart,
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
} from '@shopify/hydrogen-react'
import { ResponsiveImage } from 'components/Global/ResponsiveImage'
import { useIsMobile } from 'lib/hooks/useIsMobile'

export const MiniProductCard = ({ product, aspect, src, size = 'normal' }) => {
  return (
    <ProductProvider data={product} initialVariantId={null}>
      <div
        className={`flex flex-wrap justify-between rounded-8 bg-white p-8 text-11 leading-100 text-darkGray`}
      >
        <div
          className={`relative w-auto overflow-hidden rounded-6 ${
            size === 'normal' ? 'mr-14 h-[100px]' : 'mr-8 h-[70px]'
          }`}
          style={{
            aspectRatio: aspect,
          }}
        >
          <ResponsiveImage
            src={src}
            alt={`Product image for ${product.title}`}
            position="right"
            sizes="100px"
          />
        </div>
        <div
          className={`flex flex-1 flex-col md:flex-row ${
            size === 'xs' ? 'md:flex-col' : ''
          }`}
        >
          <div className="flex flex-1 flex-col gap-8 pt-8 text-left md:pr-20">
            <div>{product.title}</div>
            {size !== 'xs' ? (
              <div className="hidden md:block">
                {formatCurrency(product.priceRange.minVariantPrice.amount)}
              </div>
            ) : null}
          </div>
          <ProductForm
            size={size}
            price={formatCurrency(product.priceRange.minVariantPrice.amount)}
          />
        </div>
      </div>
    </ProductProvider>
  )
}

const ProductForm = ({ price, size = 'normal' }) => {
  const { cartState, setCartState } = useAppStore()
  const { status, id: cartId } = useCart()
  const [adding, setAdding] = useState(false)
  const isMobile = useIsMobile()
  const {
    options,
    isOptionInStock,
    selectedOptions,
    setSelectedOption,
    selectedVariant,
    product,
  } = useProduct()

  useEffect(() => {
    if (adding) {
      if (status === 'idle') {
        setTimeout(() => {
          setAdding(false)
        }, 2000)
      }
    }
  }, [adding, status])

  return (
    <div
      className={`mt-10 flex w-full flex-col gap-8 md:mt-0 md:w-[55%] ${
        size === 'xs' ? 'md:mt-10 md:w-full' : ''
      }`}
    >
      {options && options.length > 0 ? (
        <>
          <div className="md:block">
            <InteractiveSelector
              options={options}
              selectedOptions={selectedOptions}
              isOptionInStock={isOptionInStock}
              setSelectedOption={setSelectedOption}
              size={size}
            />
          </div>
        </>
      ) : null}
      <AddToCartButton
        accessibleAddingToCartLabel="Add to Cart"
        variantId={selectedVariant?.id}
        disabled={!selectedVariant}
        quantity={1}
        onClick={() => {
          setAdding(true)
          if (isMobile) {
            return setCartState('open')
          }
          if (cartState !== 'open') {
            setCartState('expanded')
            setTimeout(() => {
              setCartState('closed')
            }, 3000)
          }
          if (
            selectedVariant &&
            selectedVariant.priceV2?.amount &&
            cartId &&
            product
          ) {
            sendShopifyAnalytics({
              eventName: AnalyticsEventName.ADD_TO_CART,
              payload: {
                ...getClientBrowserParameters(),
                shopId: 'gid://shopify/Shop/23806877',
                hasUserConsent: true,
                cartId: cartId as string,
                currency: 'USD',
                totalValue: parseFloat(selectedVariant.priceV2.amount),
                products: [
                  {
                    productGid: product.id as string,
                    name: product.title as string,
                    brand: 'CHNGE',
                    price: selectedVariant.priceV2.amount,
                    quantity: 1,
                    variantGid: selectedVariant.id,
                    variantName: selectedVariant.title,
                  },
                ],
              },
            })
          }
        }}
        className={`flex w-full justify-between rounded-8 bg-yellow px-24 py-12 uppercase ${
          selectedVariant
            ? status === 'updating'
              ? 'cursor-wait'
              : 'cursor-pointer'
            : 'cursor-not-allowed'
        }`}
      >
        <div>{price}</div>
        <div>
          {adding && status === 'updating'
            ? 'Adding to cart'
            : adding && status === 'idle'
            ? 'Added to cart'
            : 'Add to cart'}
        </div>
      </AddToCartButton>
    </div>
  )
}

const InteractiveSelector = ({
  options,
  selectedOptions,
  isOptionInStock,
  setSelectedOption,
  size = 'normal',
}) => {
  const [interactive, setInteractive] = useState(false)
  const sizeOption = options?.find((option) => option.name === 'Size')
  return (
    <div className="relative w-full overflow-hidden">
      <button
        aria-expanded={interactive}
        className="w-full cursor-pointer rounded-8 rounded-8 bg-[#DEDEDE] px-24 py-12 text-center"
        onClick={() => setInteractive(true)}
      >
        Select Size
      </button>
      <AnimatePresence>
        {interactive ? (
          <motion.div
            className="absolute inset-0 h-full w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.35,
              ease: 'easeInOut',
            }}
          >
            <div className="flex gap-4 rounded-8 bg-white p-4">
              {sizeOption.values.map((value, i) => {
                const available = isOptionInStock(sizeOption.name, value)
                const isActive = selectedOptions
                  ? selectedOptions[sizeOption.name] === value
                  : false
                return (
                  <button
                    disabled={!available}
                    tabIndex={available ? 0 : -1}
                    className={`ease flex-1 rounded-6 px-4 py-10 transition-colors duration-200 md:px-6 ${
                      isActive && available ? 'bg-[#DEDEDE]' : 'bg-white'
                    } ${
                      available
                        ? 'hover:bg-[#DEDEDE]'
                        : 'cursor-not-allowed opacity-50'
                    }
                      ${size === 'xs' ? 'md:px-4' : 'md:px-6'}`}
                    key={`value-${value}-${i}`}
                    onClick={() => {
                      available && setSelectedOption(sizeOption.name, value)
                    }}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
